import { Component, Input, AfterViewInit, OnDestroy, ViewChild, ElementRef, OnInit, ChangeDetectorRef, inject } from "@angular/core";
import {
  TaskDto,
  ResourceDto,
  EnumNamesValuesDto,
  TaskStatusMetaDto,
  AppointmentArrangement,
} from "src/app/web-api/web-api";
import { Subscription, fromEvent, ReplaySubject } from "rxjs";
import { tap } from "rxjs/operators";
import { MasterDataService } from "src/app/services";
import { CstNavigationService } from "src/app/services/cst-navigation.service";

@Component({
    selector: "app-planung-task-card",
    templateUrl: "./task-card.component.html",
    styleUrls: ["./task-card.component.scss"],
    standalone: false
})
export class PlanungApiTaskCardComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private _nav = inject(CstNavigationService);
  private _changeDetector = inject(ChangeDetectorRef);
  private _md = inject(MasterDataService);

  appointmentArrangement = AppointmentArrangement;

  private _subscriptionDragDrop: Subscription;

  public static get DragDropKey_TaskID() {
    return "task-id";
  }

  @Input() set task(value: TaskDto) {
    this.task$.next(value);
  }

  @Input() resources: ResourceDto[] = null;
  @Input() taskStates: EnumNamesValuesDto = null;
  @Input() taskStateMetas: TaskStatusMetaDto[] = null;
  @Input() displayMode: "list" | "calendar" = "list";
  @Input() displayTaskLocation = false;
  @Input() displayTaskLocationInTitle = false;
  @Input() displayAnlageNr = false;
  @Input() displayResourceName = true;
  @Input() transparent = false;
  @Input() handlesDragDrop = true;
  @Input() showTooltip = true;
  @Input() tooltipPosition: "left" | "right" | "top" | "bottom" = "left";

  public stateMeta: TaskStatusMetaDto = null;

  @ViewChild("card") _cardElement: ElementRef;

  public task$ = new ReplaySubject<TaskDto>();

  private get isDestroyed() {
    return this._changeDetector
      ? (this._changeDetector as any).destroyed
      : true;
  }

  ngOnInit(): void {
    if (!this.resources) {
      this._md.Resources.GetResources().subscribe((result) => {
        //this._resClient.getAll().subscribe(result => {
        this.resources = result.resources;
        if (!this.isDestroyed) {
          this._changeDetector.detectChanges();
        }
      });
    }
    if (!this.taskStates) {
      this._md.Tasks.GetTaskStates().subscribe((result) => {
        //this._taskClient.getTaskStates().subscribe(result => {
        this.taskStates = result;
      });
    }

    this.task$.subscribe((task) => {
      if (this.taskStateMetas) {
        this.stateMeta = this.taskStateMetas.find(
          (tsm) => tsm.state === task.state
        );
      } else {
        this._md.Tasks.GetTaskStateMetas().subscribe((result) => {
          this.taskStateMetas = result.statusMetaInfos;
          this.stateMeta = this.taskStateMetas.find(
            (tsm) => tsm.state === task.state
          );
        });
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.handlesDragDrop && this.displayMode === "list") {
      this._subscriptionDragDrop = this._HandleDragDrop();
    }
  }
  ngOnDestroy(): void {
    if (
      this.handlesDragDrop &&
      this.displayMode === "list" &&
      this._subscriptionDragDrop
    ) {
      this._subscriptionDragDrop.unsubscribe();
    }
  }

  public HasResource(task: TaskDto) {
    if (!task.claimResourceID && !task.assignedToResourceID) {
      return false;
    }
    return true;
  }

  public GetPlannedFor(task: TaskDto) {
    if (!task || !task.dateRanges) {
      return "---";
    }
    if (task.dateRanges.todoDate) {
      return (
        (task.dateRanges.todoTime
          ? task.dateRanges.todoTime.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          }) + " - "
          : "") +
        task.dateRanges.todoDate.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      );
    }
    if (task.dateRanges.plannedWeek) {
      return task.dateRanges.plannedWeek.toString();
    }
    if (task.dateRanges.periodOfAction) {
      return task.dateRanges.periodOfAction.toShortString();
    }
    return "---";
  }
  public GetPlannedForStyle(task: TaskDto) {
    if (!task || task.dateRangesActive) {
      return null;
    }
    return {
      "font-style": "italic",
      opacity: "0.5",
    };
  }

  public GetResourceName(task: TaskDto): string {
    if (!this.resources || !task) {
      return null;
    }
    const claimed = this.GetClaimedByName(task);
    if (claimed) {
      return claimed;
    }
    return this.GetAssignedToName(task);
  }

  public GetAssignedToName(task: TaskDto): string {
    if (!this.resources || !task) {
      return null;
    }

    if (!task.assignedToResourceID) {
      return null;
    }
    const assigned = this.resources.find(
      (r) => r.id === task.assignedToResourceID
    );
    if (!assigned) {
      return null;
    }
    return assigned.name;
  }
  public GetClaimedByName(task: TaskDto): string {
    if (!this.resources || !task) {
      return null;
    }

    if (!task.claimResourceID) {
      return null;
    }
    const claimed = this.resources.find((r) => r.id === task.claimResourceID);
    if (!claimed) {
      return null;
    }
    return claimed.name;
  }

  public GetShortenedDescription(task: TaskDto): string {
    if (!task || !task.description) {
      return null;
    }
    const descLen = task.description.length;
    if (descLen > 255) {
      return task.description.substr(0, 255) + "...";
    }
    return task.description;
  }

  public OnOpenTask(task: TaskDto) {
    if (!task) {
      return;
    }
    this._nav.navigateTo_Planung_Tasks(task.taskNo);
  }

  public BuildStateStyle(stateMeta: TaskStatusMetaDto): any {
    if (!stateMeta) {
      return null;
    }
    return {
      color: stateMeta.fontColor,
      background: stateMeta.color,
    };
  }
  public BuildTextStyle(): any {
    return {
      opacity: this.transparent ? "0.7" : "1",
      fontStyle: this.transparent ? "italic" : "regular",
    };
  }
  public GetShouldDisplayAnlageNr(task: TaskDto): boolean {
    if (!this.displayTaskLocation || !task || !task.anlageNrIntern) {
      return false;
    }
    return true;
  }
  public GetShouldDisplayTaskLocation(task: TaskDto): boolean {
    if (!this.displayTaskLocation || !task || !task.workLocation) {
      return false;
    }
    const loc = task.workLocation;
    return !(!loc.street && !loc.streetNo && !loc.city && !loc.postalCode);
  }
  public GetShouldDisplayTaskLocationInTitle(task: TaskDto): boolean {
    if (
      this.displayTaskLocation ||
      !this.displayTaskLocationInTitle ||
      !task ||
      !task.workLocation
    ) {
      return false;
    }
    const loc = task.workLocation;
    return !(!loc.street && !loc.streetNo && !loc.city && !loc.postalCode);
  }
  public GetTaskLocationString(task: TaskDto): string {
    const loc = task.workLocation;
    const name = (loc.name || "").trim();
    const cityPostal = (loc.postalCode + " " + loc.city).trim();
    const streetAndNo = (loc.street + " " + loc.streetNo).trim();
    return (
      (name.length > 0 ? `${name} - ` : "") +
      cityPostal +
      (cityPostal.length > 0 ? " - " : "") +
      streetAndNo
    );
  }

  private _HandleDragDrop(): Subscription {
    const sub = new Subscription(() => { });
    const item = this._cardElement.nativeElement;
    if (item && item instanceof HTMLElement) {
      item.setAttribute("draggable", "true");
      const dragStart = fromEvent<DragEvent>(item, "dragstart");
      sub.add(
        dragStart
          .pipe(
            tap(({ dataTransfer }) => {
              dataTransfer.setData(
                PlanungApiTaskCardComponent.DragDropKey_TaskID,
                item.attributes["task-id"].value
              );
              dataTransfer.dropEffect = "move";
              dataTransfer.effectAllowed = "move";
            })
          )
          .subscribe()
      );
    }
    return sub;
  }

  public GetTooltipAssignedTo(task: TaskDto) {
    return $localize`:@@PlanungTaskCard_Tooltip_AssignedTo:This task is assigend to ${this.GetAssignedToName(
      task
    )}`;
  }
  public GetTooltipClaimedBy(task: TaskDto) {
    return $localize`:@@PlanungTaskCard_Tooltip_ClaimedByTo:This task is claimed by ${this.GetClaimedByName(
      task
    )}`;
  }
}